"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FieldHTMLAttributes = void 0;
/**
 * Calculates a base set of HTML attributes for a field
 *
 * Some attributes will be provided or overwritten at element level,
 * e.g. `checked` for a Checkbox
 */
const getFieldHTMLAttributes = (field, options) => {
    const { touchOnChange = true, validateOnChange = true, validateOnBlur = true } = options;
    return {
        name: field.getName(),
        value: field.getValue(),
        checked: false,
        defaultValue: undefined,
        defaultChecked: undefined,
        onChange: (e) => {
            touchOnChange && field.setTouched(true);
            field.setValue(e.target.value);
            validateOnChange && field.queueValidation();
        },
        onBlur: () => {
            field.setTouched(true);
            validateOnBlur && field.queueValidation();
        },
        "aria-invalid": field.hasError(),
    };
};
/** Helper methods to generate base HTML attributes for a field based on element type */
exports.FieldHTMLAttributes = {
    /**
     * Generates attributes for an input element
     * @param field The field controlling this element
     * @param options Additional options to modify touched and validation behavior
     */
    Input: (field, options = {}) => {
        return Object.assign({ ref: field.getRef() }, getFieldHTMLAttributes(field, options));
    },
    /** Generates attributes for a select element
     * @param field The field controlling this element
     * @param options Additional options to modify touched and validation behavior
     */
    Select: (field, options = {}) => {
        return Object.assign({ ref: field.getRef() }, getFieldHTMLAttributes(field, options));
    },
    /** Generates attributes for a textarea element
     * @param field The field controlling this element
     * @param options Additional options to modify touched and validation behavior
     */
    Textarea: (field, options = {}) => {
        return Object.assign({ ref: field.getRef() }, getFieldHTMLAttributes(field, options));
    },
    /**
     * Generates attributes for a checkbox element
     * @param field The field controlling this element
     * @param valuesAndOptions Values to use when checked/unchecked and touch/validation config options
     */
    Checkbox: (field, valuesAndOptions = {}) => {
        const { value = "on", uncheckedValue = "" } = valuesAndOptions, options = __rest(valuesAndOptions, ["value", "uncheckedValue"]);
        const onChange = () => {
            options.touchOnChange && field.setTouched(true);
            field.setValue(field.getValue() === value ? uncheckedValue : value);
            options.validateOnChange && field.queueValidation();
        };
        return Object.assign(Object.assign({ ref: field.getRef() }, getFieldHTMLAttributes(field, options)), { onChange, checked: field.getValue() === value, value: value });
    },
    /**
     * Generates attributes for a radio element
     * @param field The field controlling this element
     * @param value Value to use this radio option for the field is selected
     * @param options Additional options to modify touched and validation behavior
     */
    Radio: (field, value, options = {}) => {
        return Object.assign(Object.assign({ ref: field.getRef() }, getFieldHTMLAttributes(field, options)), { checked: field.getValue() === value, value: value, 
            // invalid if error and no value (e.g. required, all radio buttons set invalid) or this value
            "aria-invalid": field.hasError() && ["", value].includes(field.getValue()) });
    },
};
